<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && !applyErrorState && !getBatchFiles.length)"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && getBatchFiles.length" class="content-wrapper">
                <table-generic
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getBatchFilesHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('cards.batchfile.consult.loadingMoreText')"
                @loadMore="loadMoreHandler"
                @rowClicked="rowClickHandler($event)"
                @moreOptionsClicked="moreOptionsClickHandler"
                @downloadClicked="downloadClickedHandler"/>
            </div>
        </transition-group>
        <template #empty-state-slot>
            <!-- Empty-state -->
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getBatchFiles.length"
            illustration="emptyState/cards-flex"
            :title = "t('cards.batchfile.consult.emptyStates.flexTitle')"
            :description = "t('cards.batchfile.consult.emptyStates.subTitle')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal "
    emailAction="sendOperationEmailNotification"
    smsAction="sendOperationSMSNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="sendInfoData"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- ShareLinkModal -->
    <modal-centered-share-link
    v-if="showShareLinkModal"
    :operationId="sendInfoData.operationId"
    @hideMe="showShareLinkModal = false"/>
    <!-- Component to handle download logic-->
    <download-logic-component
    :downloadLink="downloadLink"
    @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
    <!-- Detail Modal -->
    <detail-modal
    v-if="showDetailModal"
    :selectedBatchFiles="selectedBatchFiles"
    @hideMe="showDetailModal = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import DetailModal from '@/components/features/cards/batchfile/consult/DetailModal'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue & Assets */
import { getCurrentRouteMenuAdditionalData } from '@/assets/scripts/parsers/menu'
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { iconMapper, iconColorMapper } from '@/assets/scripts/mappers/batchFiles'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestBatchFiles, clearFeatureStates } = mapActions(moduleMapper.CARDS_BATCHFILE_CONSULT)
const { getBatchFiles, getBatchFilesHasMore } = mapGetters(moduleMapper.CARDS_BATCHFILE_CONSULT)

/* Data */
const showLoader = ref(true)
const applyErrorState = ref(false)
const tableLoadingMore = ref(false)
const selectedBatchFiles = ref(null)
const showDetailModal = ref(false)
const cardGroupId = ref(null)
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showShareLinkModal = ref(false)
const downloadLink = ref(null)
const sendInfoData = ref({})

const paginationParams = ref({
	pageNumber: 1
})

/*************************************************
*                                                *
*                   Table Data                   *
*                                                *
*************************************************/
const tableHeaderData = [
    { label: t('cards.batchfile.consult.tableHeaders.submissionDate'), dataAttr: 'submissionDate', rowCellType: 'title' },
    { label: t('cards.batchfile.consult.tableHeaders.fileDescription'), dataAttr: 'fileDescription' },
    { label: t('cards.batchfile.consult.tableHeaders.registryNumber'), dataAttr: 'registryNumber' },
    { label: t('cards.batchfile.consult.tableHeaders.totalAmount'), dataAttr: 'totalAmount', rowCellType: 'amount' },
    { label: t('cards.batchfile.consult.tableHeaders.fileStatus'), dataAttr: 'fileStatus', rowCellType: 'status' },
    { dataAttr: 'download', rowCellType: 'download' },
    { dataAttr: 'moreOptions', rowCellType: 'more-options', showInMobile: true }
]

const tableStyles = [
    { width: '150px' },
    { 'text-align': 'left' },
    { width: '100px', 'text-align': 'left' },
    { 'text-align': 'right' },
    { width: '15%', 'padding-left': '32px' },
    null,
    null
]

const tableData = computed(() => {
    return getBatchFiles.value.map(batch => {
        return [
            { value: batch.customerOperationId, dataAttr: 'id' },
            { type: 'description', value: { content: getDateWithTime(batch.submitDate), classes: 'capitalize' }, dataAttr: 'submissionDate' },
            { value: batch.sourceReference, dataAttr: 'fileDescription' },
            { value: batch.totalEntries, dataAttr: 'registryNumber' },
            { type: 'amount', value: { content: batch.totalAmount, currency: batch.currency, noBalanceIcon: true }, dataAttr: 'totalAmount' },
            { type: 'status', value: { text: batch.statusDescription || '-', icon: iconMapper(batch.status), classes: iconColorMapper(batch.status) }, dataAttr: 'fileStatus' },
            { type: 'download', dataAttr: 'download', hidden: !batch.hasReceipt },
            { type: 'more-options', value: moreOptions, dataAttr: 'tableOptions', hidden: !batch.hasReceipt }
        ]
    })
})

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    initialRequests()
}

const rowClickHandler = row => {
	selectedBatchFiles.value = getBatchFiles.value.find(cardless => cardless.customerOperationId === row.rowId)

	showDetailModal.value = true
}

const moreOptionsClickHandler = ev => {
    sendInfoData.value = {
        operationId: ev.rowId
    }
	moreOptions.find(obj => obj.id === ev.optionClicked).callbackFunction()
}

const downloadClickedHandler = ev => {
    downloadLink.value = createDownloadGenericReceiptPdf({
        params: {
            operationID: ev
        }
    })
}

const moreOptions = [
    {
        id: 1,
        text: t('cards.batchfile.consult.moreOptions.sendEmail'),
        icon: 'letter',
        callbackFunction: () => { showSendEmailInfoModal.value = true }
    }, {
        id: 2,
        text: t('cards.batchfile.consult.moreOptions.sendSms'),
        icon: 'chat',
        callbackFunction: () => { showSendSMSInfoModal.value = true }
    }, {
        id: 3,
        text: t('cards.batchfile.consult.moreOptions.generateLink'),
        icon: 'link',
        callbackFunction: () => { showShareLinkModal.value = true }
    }
]

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
    showFeedbackToast.value = false
    cancelAllPendingRequests()

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

    const payload = {
        ...paginationParams.value,
        cardGroupId: cardGroupId.value,
        pageSize: 10
    }

    try {
        await requestBatchFiles(payload)
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    }
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

onBeforeUnmount(() => {
    clearFeatureStates()
})

onMounted(() => {
    if (getCurrentRouteMenuAdditionalData().cardGroup) {
        cardGroupId.value = getCurrentRouteMenuAdditionalData().cardGroup.id
    }

    cardGroupId.value ? initialRequests() : applyErrorState.value = true
})
</script>

<style lang="scss" scoped>
</style>
